/**
 * Central configuration file for the web UI
 * 
 * This file defines the domain and port settings for all server components.
 * Supports multiple deployment environments via DEPLOYMENT_ENV variable:
 * - development: Local development (localhost)
 * - beta: Beta environment (*.ecomevolutionpro.com)
 * - production: Production environment (*.geteditify.com)
 */

// Environment detection
const environment = process.env.NODE_ENV || 'development';
const deploymentEnv = process.env.REACT_APP_DEPLOYMENT_ENV || process.env.DEPLOYMENT_ENV || 'development';

console.log('🔧 Config loaded:', { NODE_ENV: environment, DEPLOYMENT_ENV: deploymentEnv });

// Get domain from environment variables or use defaults
const getDomain = (defaultDomain) => {
  if (deploymentEnv === 'development') return 'localhost';
  return defaultDomain;
};

// Server configuration
const servers = {
  development: {
    proxy: {
      protocol: 'http',
      domain: 'localhost',
      port: 3001,
      path: ''
    },
    website: {
      protocol: 'http',
      domain: 'localhost',
      port: 3002,
      path: ''
    },
    imageProcessing: {
      protocol: 'http',
      domain: 'localhost',
      port: 3003,
      path: ''
    },
    stripe: {
      protocol: 'http',
      domain: 'localhost',
      port: 3004,
      path: ''
    }
  },
  beta: {
    proxy: {
      protocol: 'https',
      domain: process.env.REACT_APP_DOMAIN_API || 'api-beta.ecomevolutionpro.com',
      port: 443,
      path: ''
    },
    website: {
      protocol: 'https',
      domain: process.env.REACT_APP_DOMAIN_MAIN || 'beta.ecomevolutionpro.com',
      port: 443,
      path: ''
    },
    imageProcessing: {
      protocol: 'https',
      domain: process.env.REACT_APP_DOMAIN_IMAGE || 'images-beta.ecomevolutionpro.com',
      port: 443,
      path: ''
    },
    stripe: {
      protocol: 'https',
      domain: process.env.REACT_APP_DOMAIN_PAYMENT || 'payment-beta.ecomevolutionpro.com',
      port: 443,
      path: ''
    }
  },
  production: {
    proxy: {
      protocol: 'https',
      domain: process.env.REACT_APP_DOMAIN_API || 'api.geteditify.com',
      port: 443,
      path: ''
    },
    website: {
      protocol: 'https',
      domain: process.env.REACT_APP_DOMAIN_MAIN || 'geteditify.com',
      port: 443,
      path: ''
    },
    imageProcessing: {
      protocol: 'https',
      domain: process.env.REACT_APP_DOMAIN_IMAGE || 'imageserver.geteditify.com',
      port: 443,
      path: ''
    },
    stripe: {
      protocol: 'https',
      domain: process.env.REACT_APP_DOMAIN_PAYMENT || 'stripe.geteditify.com',
      port: 443,
      path: ''
    }
  }
};

// Firebase configuration
const firebase = {
  development: {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID
  },
  production: {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID
  }
};

/**
 * Get the full URL for a specific server type
 * @param {string} serverType - The type of server (proxy, website, imageProcessing, stripe)
 * @returns {string} The complete URL including protocol, domain, and port
 */
function getServerUrl(serverType) {
  const env = deploymentEnv; // Use deploymentEnv instead of environment
  const server = servers[env][serverType];
  
  if (!server) {
    console.error(`❌ Server config not found for: ${serverType} in ${env} environment`);
    return '';
  }
  
  let url = `${server.protocol}://${server.domain}`;
  
  // Add port if specified and not the default for the protocol
  // In development mode, always include the port
  // In production/beta mode, only include the port if it's not a standard port (80 for HTTP, 443 for HTTPS)
  // Never include port for proxy server in production/beta mode
  if (server.port && (
    (env === 'development') || 
    ((env === 'production' || env === 'beta') && serverType !== 'proxy' && (
      (server.protocol === 'http' && server.port !== 80) || 
      (server.protocol === 'https' && server.port !== 443)
    ))
  )) {
    url += `:${server.port}`;
  }
  
  // Add path if specified
  if (server.path) {
    // Ensure path starts with a slash
    const path = server.path.startsWith('/') ? server.path : `/${server.path}`;
    url += path;
  }
  
  return url;
}

/**
 * Get the port for a specific server type
 * @param {string} serverType - The type of server (proxy, website, imageProcessing, stripe)
 * @returns {number|null} The port number or null if using default port
 */
function getServerPort(serverType) {
  const env = deploymentEnv;
  return servers[env][serverType]?.port;
}

/**
 * Get the domain for a specific server type
 * @param {string} serverType - The type of server (proxy, website, imageProcessing, stripe)
 * @returns {string} The domain
 */
function getServerDomain(serverType) {
  const env = deploymentEnv;
  return servers[env][serverType]?.domain;
}

/**
 * Get the protocol for a specific server type
 * @param {string} serverType - The type of server (proxy, website, imageProcessing, stripe)
 * @returns {string} The protocol (http or https)
 */
function getServerProtocol(serverType) {
  const env = deploymentEnv;
  return servers[env][serverType]?.protocol;
}

/**
 * Get the path for a specific server type
 * @param {string} serverType - The type of server (proxy, website, imageProcessing, stripe)
 * @returns {string} The path
 */
function getServerPath(serverType) {
  const env = deploymentEnv;
  return servers[env][serverType]?.path;
}

/**
 * Get the Firebase configuration for the current environment
 * @returns {object} The Firebase configuration object
 */
function getFirebaseConfig() {
  return firebase[environment];
}

module.exports = {
  environment,
  deploymentEnv,
  servers,
  firebase,
  getServerUrl,
  getServerPort,
  getServerDomain,
  getServerProtocol,
  getServerPath,
  getFirebaseConfig
};
